import { createHttpClient } from '@shared/adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

interface UseEntriesCountByStatusProps {
  /** Término de búsqueda */
  search?: string;
  /** Filtros avanzados en formato {filters: [], logic: string} */
  advancedFilters?: { filters: any[]; logic: string };
  /** IDs de producto como param directo product_ids=1,2,3 */
  productIds?: number[];
  /** Si está habilitado el query */
  enabled?: boolean;
}

const PRODUCT_FILTER_COLUMNS = new Set(['product_ids', 'product_id']);

function withoutProductFilters(advancedFilters?: { filters: any[]; logic: string }) {
  if (!advancedFilters) {
    return undefined;
  }

  const filters = advancedFilters.filters.filter((f) => !PRODUCT_FILTER_COLUMNS.has(f.column));

  if (filters.length === 0) {
    return undefined;
  }

  return {
    ...advancedFilters,
    filters,
  };
}

/**
 * Construye los parámetros de query para advanced_filters
 */
function buildAdvancedFiltersParams(advancedFilters: {
  filters: Array<{ column: string; operator: string; value?: any }>;
  logic: string;
}): Record<string, any> {
  const params: Record<string, any> = {};

  if (advancedFilters.filters && Array.isArray(advancedFilters.filters)) {
    advancedFilters.filters.forEach((f, i) => {
      params[`advanced_filters[filters][${i}][column]`] = f.column;
      params[`advanced_filters[filters][${i}][operator]`] = f.operator;
      if (f.value !== undefined && f.value !== null && !(Array.isArray(f.value) && f.value.length === 0)) {
        if (Array.isArray(f.value)) {
          f.value.forEach((v: any, j: number) => {
            params[`advanced_filters[filters][${i}][value][${j}]`] = v;
          });
        } else {
          params[`advanced_filters[filters][${i}][value]`] = f.value;
        }
      }
    });
  }

  if (advancedFilters.logic) {
    params['advanced_filters[logic]'] = advancedFilters.logic;
  }

  return params;
}

/**
 * Hook para obtener el recuento de entries por status
 * Respeta los filtros aplicados (búsqueda y filtros avanzados)
 *
 * NOTA: Este hook espera que exista el endpoint GET /entries/count-by-status
 * Si el endpoint no existe, retornará un objeto vacío.
 */
export const useEntriesCountByStatus = ({
  search,
  advancedFilters,
  productIds = [],
  enabled = true,
}: UseEntriesCountByStatusProps = {}) => {
  const advancedFiltersWithoutProducts = useMemo(() => withoutProductFilters(advancedFilters), [advancedFilters]);

  // Crear httpClient
  const httpClient = useMemo(() => createHttpClient(apiClientWH), []);

  // Query key incluye los filtros para refetch cuando cambien
  const queryKey = useMemo(
    () => ['entries-count-by-status', search, JSON.stringify(advancedFiltersWithoutProducts), productIds.join(',')],
    [search, advancedFiltersWithoutProducts, productIds],
  );

  const {
    data: counts = {},
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      const params: Record<string, any> = {};

      // Búsqueda simple
      if (search) {
        params.search = search;
      }

      // Filtros avanzados - usar formato de query params anidados
      if (advancedFiltersWithoutProducts && advancedFiltersWithoutProducts.filters.length > 0) {
        const advancedFiltersParams = buildAdvancedFiltersParams(advancedFiltersWithoutProducts);
        Object.assign(params, advancedFiltersParams);
      }

      if (productIds.length > 0) {
        params.product_ids = productIds.join(',');
      }

      try {
        const response = await httpClient.get<Record<string, number>>('/entries/count-by-status', params);
        return response;
      } catch (error) {
        // Si el endpoint no existe, retornar objeto vacío silenciosamente
        console.warn('Endpoint /entries/count-by-status no disponible:', error);
        return {};
      }
    },
    enabled,
    staleTime: 30 * 1000, // 30 segundos de cache
    refetchOnWindowFocus: false,
    retry: false, // No reintentar si falla (el endpoint podría no existir)
  });

  return {
    counts,
    isLoading,
    error,
    refetch,
  };
};
